import { useState, useEffect } from 'react';
import apiUser from '../../apiUser';
import { fmtBRL, fmtComp, nomeContrato } from './format';

// Ajustes retroativos pendentes (spec 2026-10-19): evento com data dentro de ciclo
// já aprovado/faturado. Nada vai ao cliente sem aprovação aqui; aprovado, entra no
// próximo ciclo do contrato ainda não enviado.
const dataCurta = (d) => (d ? `${String(d).slice(8, 10)}/${String(d).slice(5, 7)}/${String(d).slice(0, 4)}` : '—');
const STATUS_CICLO = { faturado: 'faturado', aprovado: 'aprovado' };

const AjustesRetroativos = ({ aviso, onMudou }) => {
    const [pendentes, setPendentes] = useState([]);
    const [form, setForm] = useState({}); // id -> { valor, motivo }
    const [salvando, setSalvando] = useState(null);

    const carregar = async () => {
        try {
            const { data } = await apiUser.get('/faturamento/ajustes-retroativos', { params: { status: 'pendente' } });
            if (data.success) setPendentes(data.dados || []);
        } catch { /* silencioso: o painel segue sem o card */ }
    };
    useEffect(() => { carregar(); }, []);

    if (!pendentes.length) return null;

    const campo = (id, k) => form[id]?.[k] ?? '';
    const setCampo = (id, k, v) => setForm({ ...form, [id]: { ...form[id], [k]: v } });

    const decidir = async (a, acao) => {
        const f = form[a.id] || {};
        const payload = acao === 'aprovar'
            ? { valor: f.valor === undefined || f.valor === '' ? undefined : Number(String(f.valor).replace(',', '.')), motivo: f.motivo }
            : { motivo: f.motivo };
        setSalvando(a.id);
        try {
            const { data } = await apiUser.post(`/faturamento/ajustes-retroativos/${a.id}/${acao}`, payload);
            aviso(data.message, data.success ? 'success' : 'warning');
            if (data.success) { carregar(); onMudou && onMudou(); }
        } catch { aviso('Erro ao comunicar com o servidor.'); }
        setSalvando(null);
    };

    return (
        <div className='mb-5 rounded-lg border border-yellow-300 bg-yellow-50 dark:bg-yellow-900/30 dark:border-yellow-700 p-4'>
            <p className='text-sm font-semibold text-yellow-900 dark:text-yellow-100'>
                ⚠ {pendentes.length} ajuste(s) retroativo(s) aguardando aprovação
            </p>
            <p className='text-xs text-yellow-900/80 dark:text-yellow-100/80 mb-3'>
                Evento lançado com data num ciclo já fechado. Aprovado, o valor entra como linha no próximo ciclo do contrato ainda não enviado ao cliente.
            </p>
            <div className='flex flex-col gap-3'>
                {pendentes.map((a) => (
                    <div key={a.id} className='rounded-lg bg-white dark:bg-gray-800 border border-yellow-200 dark:border-gray-600 p-3 text-sm'>
                        <div className='flex flex-wrap items-baseline justify-between gap-2'>
                            <span className='font-semibold text-gray-900 dark:text-gray-100'>
                                {a.cliente_nome}{a.contrato_nome ? ` — ${nomeContrato(a)}` : ''}
                            </span>
                            <span className='text-gray-900 dark:text-gray-100'>
                                Proposto: <b>{fmtBRL(a.valor_proposto)}</b>
                            </span>
                        </div>
                        <p className='text-gray-700 dark:text-gray-300 mt-1'>
                            {a.tipo_evento}{a.percentual != null ? ` ${String(Number(a.percentual)).replace('.', ',')}%` : ''} com data de {dataCurta(a.data_evento)} ·
                            ciclo de {fmtComp(a.competencia_origem)} {STATUS_CICLO[a.status_ciclo_origem] || a.status_ciclo_origem} por {fmtBRL(a.valor_ciclo_origem)} ·
                            {' '}{fmtBRL(a.delta_mensal)}/mês × {a.dias_cobrados} dias
                            {a.diff_ao_vivo != null ? ` · diferença ao vivo do ciclo: ${fmtBRL(a.diff_ao_vivo)}` : ''}
                        </p>
                        <div className='flex flex-wrap items-end gap-2 mt-2'>
                            <div>
                                <label className='block text-xs text-gray-600 dark:text-gray-400 mb-1'>Valor (R$)</label>
                                <input type='text' inputMode='decimal' value={campo(a.id, 'valor')} placeholder={String(a.valor_proposto).replace('.', ',')}
                                    onChange={(e) => setCampo(a.id, 'valor', e.target.value)}
                                    className='border rounded-lg p-1.5 w-32 dark:bg-gray-700 dark:text-white' />
                            </div>
                            <div className='flex-1 min-w-[200px]'>
                                <label className='block text-xs text-gray-600 dark:text-gray-400 mb-1'>Motivo (obrigatório para mudar o valor ou descartar)</label>
                                <input type='text' value={campo(a.id, 'motivo')} onChange={(e) => setCampo(a.id, 'motivo', e.target.value)}
                                    className='border rounded-lg p-1.5 w-full dark:bg-gray-700 dark:text-white' />
                            </div>
                            <button disabled={salvando === a.id} onClick={() => decidir(a, 'aprovar')}
                                className='px-3 py-1.5 rounded-lg text-white font-semibold whitespace-nowrap disabled:opacity-50' style={{ backgroundColor: '#9ac31c' }}>
                                Aprovar
                            </button>
                            <button disabled={salvando === a.id} onClick={() => decidir(a, 'descartar')}
                                className='px-3 py-1.5 rounded-lg bg-gray-200 hover:bg-gray-300 dark:bg-gray-600 dark:text-white whitespace-nowrap disabled:opacity-50'>
                                Descartar
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default AjustesRetroativos;
